import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  Chip,
  Stack,
  Typography,
  LinearProgress,
} from "@mui/material";
import {
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon,
  TableChart as CsvIcon,
  Print as PrintIcon,
  InboxOutlined as EmptyIcon,
} from "@mui/icons-material";
import { ifasColors } from "../../components/OperationsUI";

const PgButton: React.FC<{ active?: boolean; disabled?: boolean; onClick?: () => void; children: React.ReactNode }> = ({ active, disabled, onClick, children }) => (
  <Box
    component="button"
    disabled={disabled}
    onClick={disabled ? undefined : onClick}
    sx={{
      minWidth: 30,
      height: 30,
      px: 0.75,
      borderRadius: "8px",
      cursor: disabled ? "default" : "pointer",
      border: `1px solid ${active ? "transparent" : ifasColors.border}`,
      bgcolor: active ? ifasColors.orange : ifasColors.surface,
      color: active ? "#FFFFFF" : ifasColors.text,
      fontFamily: "inherit",
      fontSize: "0.78rem",
      fontWeight: 800,
      opacity: disabled ? 0.4 : 1,
      transition: "background .12s",
      "&:hover": { bgcolor: active ? ifasColors.orange : ifasColors.softOrange },
    }}
  >
    {children}
  </Box>
);

export interface ColumnDef<T> {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "left" | "center";
  format?: (value: any, row: T) => React.ReactNode;
}

interface Props<T> {
  title: string;
  subtitle?: string;
  columns: ColumnDef<T>[];
  data: T[];
  loading?: boolean;
  onExport?: (format: "excel" | "pdf" | "csv") => void;
}

export function IFASReportTable<T extends Record<string, any>>({
  title,
  subtitle,
  columns,
  data,
  loading = false,
  onExport,
}: Props<T>) {
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const handlePrint = () => {
    window.print();
  };

  const total = data.length;
  const pageCount = Math.max(1, Math.ceil(total / rowsPerPage));
  const currentPage = Math.min(page, pageCount - 1);
  const paginatedData = data.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);
  const rangeStart = total === 0 ? 0 : currentPage * rowsPerPage + 1;
  const rangeEnd = Math.min((currentPage + 1) * rowsPerPage, total);
  const pageNumbers: number[] = pageCount <= 7
    ? Array.from({ length: pageCount }, (_, i) => i)
    : Array.from(
        new Set([0, 1, currentPage - 1, currentPage, currentPage + 1, pageCount - 2, pageCount - 1].filter((n) => n >= 0 && n < pageCount)),
      ).sort((a, b) => a - b);

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        overflow: "hidden",
        borderRadius: "16px",
        border: `1px solid ${ifasColors.border}`,
        boxShadow: "0 8px 24px rgba(15,23,42,0.045)",
        mb: 3,
        background: ifasColors.surface,
      }}
    >
      <Box
        sx={{
          p: { xs: 2, md: 2.5 },
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: `1px solid ${ifasColors.border}`,
          background: ifasColors.surface,
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ fontSize: "1rem", fontWeight: 800, color: ifasColors.text }}>{title}</Typography>
            <Chip label={`${data.length} records`} size="small" sx={{ height: 23, bgcolor: ifasColors.page, color: ifasColors.muted, fontSize: "0.66rem", fontWeight: 700 }} />
          </Stack>
          {subtitle && (
            <Typography sx={{ mt: 0.4, color: ifasColors.muted, fontSize: "0.74rem" }}>
              {subtitle}
            </Typography>
          )}
        </Box>

        <Stack direction="row" spacing={0.75} sx={{ overflowX: "auto", maxWidth: "100%" }}>
          <Button
            startIcon={<ExcelIcon />}
            onClick={() => onExport && onExport("excel")}
            sx={{
              bgcolor: "#ECFDF5", color: "#047857 !important", border: "1px solid #A7F3D0",
              fontWeight: 700, textTransform: "none", px: 1.4, minWidth: "auto", borderRadius: "9px", fontSize: "0.7rem", lineHeight: 2.4,
              "&:hover": { bgcolor: "#D1FAE5" },
            }}
          >
            Excel
          </Button>
          <Button
            startIcon={<PdfIcon />}
            onClick={() => onExport && onExport("pdf")}
            sx={{
              bgcolor: "#FEF2F2", color: "#B91C1C !important", border: "1px solid #FECACA",
              fontWeight: 700, textTransform: "none", px: 1.4, minWidth: "auto", borderRadius: "9px", fontSize: "0.7rem", lineHeight: 2.4,
              "&:hover": { bgcolor: "#FEE2E2" },
            }}
          >
            PDF
          </Button>
          <Button
            startIcon={<CsvIcon />}
            onClick={() => onExport && onExport("csv")}
            sx={{
              bgcolor: "#EFF6FF", color: "#1D4ED8 !important", border: "1px solid #BFDBFE",
              fontWeight: 700, textTransform: "none", px: 1.4, minWidth: "auto", borderRadius: "9px", fontSize: "0.7rem", lineHeight: 2.4,
              "&:hover": { bgcolor: "#DBEAFE" },
            }}
          >
            CSV
          </Button>
          <Button
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{
              bgcolor: "#F8FAFC", color: "#475569 !important", border: "1px solid #CBD5E1",
              fontWeight: 700, textTransform: "none", px: 1.4, minWidth: "auto", borderRadius: "9px", fontSize: "0.7rem", lineHeight: 2.4,
              "&:hover": { bgcolor: "#F1F5F9" },
            }}
          >
            Print
          </Button>
        </Stack>
      </Box>

      {loading && <LinearProgress color="primary" sx={{ height: 3 }} />}

      <TableContainer sx={{ maxHeight: 540 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || "left"}
                  sx={{
                    minWidth: column.minWidth,
                    fontWeight: 800,
                    backgroundColor: ifasColors.page,
                    color: ifasColors.muted,
                    fontSize: "0.69rem",
                    letterSpacing: "0.6px",
                    textTransform: "uppercase",
                    borderBottom: `1px solid ${ifasColors.border}`,
                    py: 1.5,
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length > 0 ? (
              paginatedData.map((row, index) => (
                <TableRow
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  key={index}
                  sx={{
                    "&:nth-of-type(even)": { backgroundColor: ifasColors.page },
                    "&:hover": { backgroundColor: `${ifasColors.softOrange} !important` },
                  }}
                >
                  {columns.map((column) => {
                    const value = row[column.id];
                    return (
                      <TableCell key={column.id} align={column.align || "left"} sx={{ py: 1.85, borderColor: ifasColors.border, fontSize: "0.8rem", color: ifasColors.text, fontWeight: 600 }}>
                        {column.format ? column.format(value, row) : value ?? "-"}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 7 }}>
                  <Box sx={{ width: 48, height: 48, borderRadius: "14px", bgcolor: ifasColors.page, display: "grid", placeItems: "center", mx: "auto", mb: 1.5 }}><EmptyIcon sx={{ color: ifasColors.muted }} /></Box>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: ifasColors.text }}>{loading ? "Loading report data..." : "No records found"}</Typography>
                  {!loading && <Typography sx={{ mt: 0.5, fontSize: "0.72rem", color: ifasColors.muted }}>Try changing or clearing the selected filters.</Typography>}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap", p: 2, borderTop: `1px solid ${ifasColors.border}`, bgcolor: ifasColors.page }}>
        <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: ifasColors.muted }}>
          {total === 0 ? "No records to show" : `Showing ${rangeStart}–${rangeEnd} of ${total}`}
        </Typography>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <PgButton disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>‹</PgButton>
          {pageNumbers.map((n, i) => {
            const showGap = i > 0 && n - pageNumbers[i - 1] > 1;
            return (
              <React.Fragment key={n}>
                {showGap && <Typography sx={{ color: ifasColors.muted, px: 0.25, fontWeight: 800 }}>…</Typography>}
                <PgButton active={n === currentPage} onClick={() => setPage(n)}>{n + 1}</PgButton>
              </React.Fragment>
            );
          })}
          <PgButton disabled={currentPage >= pageCount - 1} onClick={() => setPage(currentPage + 1)}>›</PgButton>
        </Stack>
      </Box>
    </Paper>
  );
}
